import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { InfoIcon } from "lucide-react";
import { useUpdateExchangeRate } from "@/hooks/useExchangeRateQueries";
import {
  formatExchangeRate,
  formatConversionExample,
  isValidExchangeRate,
  getCurrencyFlag,
} from "@/lib/currencyUtils";

// Supported currencies (excluding VND as it's the base currency)
const SUPPORTED_CURRENCIES = ["USD", "EUR", "GBP", "JPY", "KWD"];

interface CreateExchangeRateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Create Exchange Rate Dialog
 *
 * Allows admins to create a new exchange rate for a currency.
 * Uses the same PUT endpoint as update (idempotent - creates if doesn't exist).
 *
 * Features:
 * - Currency selection dropdown
 * - Real-time preview of new rate
 * - Example conversion (₫1,000,000 = X Currency)
 * - Optional notes for audit trail
 * - Validation for rate > 0
 */
export function CreateExchangeRateDialog({
  open,
  onClose,
  onSuccess,
}: CreateExchangeRateDialogProps) {
  const [currency, setCurrency] = useState<string>("");
  const [newRate, setNewRate] = useState("");
  const [notes, setNotes] = useState("");
  const [validationError, setValidationError] = useState("");

  const { mutate: updateRate, isPending } = useUpdateExchangeRate();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCurrency("");
      setNewRate("");
      setNotes("");
      setValidationError("");
    }
  }, [open]);

  const handleRateChange = (value: string) => {
    setNewRate(value);
    setValidationError("");

    // Validate rate
    const numericRate = parseFloat(value);
    if (value && (!isValidExchangeRate(numericRate) || numericRate <= 0)) {
      setValidationError("Exchange rate must be a positive number");
    }
  };

  const handleSubmit = async () => {
    if (!currency) {
      setValidationError("Please select a currency");
      return;
    }

    const numericRate = parseFloat(newRate);

    // Validate
    if (!newRate || !isValidExchangeRate(numericRate) || numericRate <= 0) {
      setValidationError("Please enter a valid exchange rate");
      return;
    }

    try {
      await updateRate.mutateAsync({
        currency,
        request: {
          rate: numericRate,
          source: "Manual",
          notes: notes.trim() || undefined,
        },
      });
      onSuccess();
      onClose();
    } catch {
      // Error handling is done in the mutation via toast
    }
  };

  const numericNewRate = parseFloat(newRate);
  const isRateValid =
    newRate && isValidExchangeRate(numericNewRate) && numericNewRate > 0;
  const isFormValid = currency && isRateValid;
  const isDirty = currency || newRate || notes;

  const guard = useCloseGuard({
    open,
    onOpenChange: onClose,
    isDirty,
    isSubmitting: isPending,
    onDiscard: () => {
      setCurrency("");
      setNewRate("");
      setNotes("");
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Exchange Rate</DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <DialogDescription>
              Create a new exchange rate for a currency. The rate will be
              applied immediately system-wide.
            </DialogDescription>
            {/* Currency Selection */}
            <div className="space-y-2">
              <Label htmlFor="currency">
                Currency <span className="text-destructive-ink">*</span>
              </Label>
              <Select
                value={currency}
                onValueChange={(value) => {
                  setCurrency(value);
                  setValidationError("");
                }}
              >
                <SelectTrigger id="currency">
                  <SelectValue placeholder="Select a currency" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((curr) => (
                    <SelectItem key={curr} value={curr}>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getCurrencyFlag(curr)}</span>
                        <span>{curr}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Info Alert */}
            {currency && (
              <Alert>
                <InfoIcon className="h-4 w-4" />
                <AlertTitle>Base Currency</AlertTitle>
                <AlertDescription>
                  All exchange rates are based on VND (Vietnamese Dong) as the
                  base currency.
                </AlertDescription>
              </Alert>
            )}

            {/* New Rate Input */}
            {currency && (
              <div className="space-y-2">
                <Label htmlFor="newRate">
                  Exchange Rate <span className="text-destructive-ink">*</span>
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    1 {currency} =
                  </span>
                  <Input
                    id="newRate"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="23250.00"
                    value={newRate}
                    onChange={(e) => handleRateChange(e.target.value)}
                    className={validationError ? "border-destructive" : ""}
                  />
                  <span className="text-sm text-muted-foreground">VND</span>
                </div>
                {validationError && (
                  <p className="text-sm text-destructive-ink">
                    {validationError}
                  </p>
                )}
              </div>
            )}

            {/* Real-time Preview */}
            {isRateValid && currency && (
              <Card className="p-4 space-y-3 bg-muted/50">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Rate Preview</p>
                  <p className="font-mono text-sm font-medium">
                    {formatExchangeRate(currency, numericNewRate)}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Example Conversion
                  </p>
                  <p className="text-sm">
                    {formatConversionExample(1000000, currency, numericNewRate)}
                  </p>
                </div>
              </Card>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add a note explaining this rate..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                {notes.length}/500 characters
              </p>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!isFormValid || isPending}>
              {isPending ? "Creating..." : "Create Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
